import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  API_ADD_PRODUCT,
  API_GET_PRODUCTS,
  API_PUT_PRODUCT,
  API_DELETE_PRODUCT,
  API_GET_PRODUCT_ID,
  API_GET_PRODUCTS_SORT,
  API_BULK_DELETE_PRODUCT,
  API_BULK_PRODUCT_ADD,
} from "../api/product.services";
import {
  removeDeleteData,
  updateProductTable,
  getUnselectedProducts,
  isProductInList,
} from "../utils/tableUtils";

const initialState = {
  productData: [],
  totalProductCount: 0,
  loading: false,
  selectedProduct: null,
  page: 0,
  limit: 30,
  mode: "create",
  selectedProductsList: [],
  selectedProductBlist: [],
};

export const getProducts = createAsyncThunk(
  "productTable/getProducts",
  async ({ page, limit, filterData, globalFilterValue }, thunkAPI) => {
    try {
      let products = await API_GET_PRODUCTS(
        page,
        limit,
        filterData,
        globalFilterValue
      );
      return products;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getSortedProducts = createAsyncThunk(
  "productTable/getsortedProducts",
  async (
    { page, limit, filterData, globalFilterValue, productIds = [] },
    thunkAPI
  ) => {
    try {
      let products = await API_GET_PRODUCTS_SORT(
        page,
        limit,
        filterData,
        globalFilterValue,
        productIds
      );
      return products;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const addProduct = createAsyncThunk(
  "productTable/addProduct",
  async ({ data }, thunkAPI) => {
    try {
      let product = await API_ADD_PRODUCT(data);
      return product;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const updateProduct = createAsyncThunk(
  "productTable/updateProduct",
  async ({ productId, data }, thunkAPI) => {
    try {
      let res = await API_PUT_PRODUCT(productId, data);
      return res;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getProductbyId = createAsyncThunk(
  "productTable/getProductsbyId",
  async ({ productId }, thunkAPI) => {
    try {
      let res = await API_GET_PRODUCT_ID(productId);
      return res;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "productTable/deleteProduct",
  async (productId, thunkAPI) => {
    try {
      await API_DELETE_PRODUCT(productId);
      return productId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const bulkDeleteProduct = createAsyncThunk(
  "productTable/bulkProductDelete",
  async (prodIds, thunkAPI) => {
    try {
      await API_BULK_DELETE_PRODUCT(prodIds);
      return prodIds;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const bulkProductAdd = createAsyncThunk(
  "product/bulkproductAdd",
  async (newProducts, thunkAPI) => {
    try {
      let data = await API_BULK_PRODUCT_ADD(newProducts);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const productTableSlice = createSlice({
  name: "productTable",
  initialState,
  reducers: {
    changeMode(state, action) {
      state.mode = action.payload;
    },

    resetMode(state) {
      state.mode = null;
    },
    setproduct(state, action) {
      if (state.productData.length < state.limit) {
        state.productData = [action.payload, ...state.productData];
      }
      state.totalProductCount += 1;
    },
    changeSelectedProduct(state, action) {
      // console.log(action.payload)
      state.selectedProduct = action.payload;
    },

    resetSelectedProduct(state) {
      state.selectedProduct = null;
    },

    changePage(state, action) {
      state.page = action.payload;
    },
    changelimit(state, action) {
      state.limit = action.payload;
    },
    changeSelectedProductsList(state, action) {
      // console.log(action.payload)
      state.selectedProductsList = action.payload;
    },
    changeSelectedProductBlist(state, action) {
      // console.log(action.payload)
      state.selectedProductBlist = action.payload;
    },
    resetSelectedProductBlist(state) {
      state.selectedProductBlist = [];
    },
    updateSelectedProductsList(state, action) {
      const { selectedProducts, currProducts } = action.payload;
      if (!state.selectedProductsList?.length) {
        state.selectedProductsList = selectedProducts;
      } else {
        const newItems = selectedProducts.filter((product) => {
          if (!isProductInList(state.selectedProductsList, product))
            return product;
        });
        const unselectedProducts = getUnselectedProducts(
          currProducts,
          selectedProducts
        );

        const onlySelectedItems = state.selectedProductsList.filter(
          (product) => {
            if (!isProductInList(unselectedProducts, product)) return product;
          }
        );
        state.selectedProductsList = [...onlySelectedItems, ...newItems];
      }
    },
    updateSelectedProductsBList(state, action) {
      const { selectedProducts, currProducts } = action.payload;
      if (!state.selectedProductBlist?.length) {
        state.selectedProductBlist = selectedProducts;
      } else {
        const newItems = selectedProducts.filter((product) => {
          if (!isProductInList(state.selectedProductBlist, product))
            return product;
        });
        const unselectedProducts = getUnselectedProducts(
          currProducts,
          selectedProducts
        );

        const onlySelectedItems = state.selectedProductBlist.filter(
          (product) => {
            if (!isProductInList(unselectedProducts, product)) return product;
          }
        );
        state.selectedProductBlist = [...onlySelectedItems, ...newItems];
      }
    },

    resetSelectedProductsList(state) {
      state.selectedProductsList = [];
    },
    resetAll(state) {
      state.selectedProductsList = initialState.selectedProductsList;
      state.productData = initialState.productData;
      state.page = initialState.page;
      state.limit = initialState.limit;
      state.selectedProduct = initialState.selectedProduct;
      state.mode = initialState.mode;
    },
    removeProductFromProductsBList(state, action) {
      let removeProductSKUCode = action.payload;

      let currSelectedProductsForBroadcast = [...state.selectedProductBlist];
      state.selectedProductBlist = currSelectedProductsForBroadcast.filter(
        (prod) => prod.SKUCode != removeProductSKUCode
      );
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getProducts.fulfilled, (state, action) => {
      state.totalProductCount = action.payload.count;
      state.productData = action.payload.rows;
      state.loading = false;
    });
    builder.addCase(getProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProducts.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getSortedProducts.fulfilled, (state, action) => {
      state.totalProductCount = action.payload.count;
      state.productData = action.payload.rows;
      state.loading = false;
    });
    builder.addCase(getSortedProducts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSortedProducts.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getProductbyId.fulfilled, (state, action) => {
      state.selectedProduct = action.payload;
      state.loading = false;
    });
    builder.addCase(getProductbyId.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProductbyId.rejected, (state) => {
      state.loading = false;
    });

    //add product

    builder.addCase(addProduct.fulfilled, (state, action) => {
      let data = action.payload;
      if (state.productData.length < state.limit) {
        state.productData = [data, ...state.productData];
      } else {
        state.productData = [
          data,
          ...state.productData.slice(0, state.limit - 1),
        ];
      }

      state.totalProductCount += 1;
      state.loading = false;
    });

    builder.addCase(addProduct.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(addProduct.rejected, (state) => {
      state.loading = false;
    });

    // update product
    builder.addCase(updateProduct.fulfilled, (state, action) => {
      const d = new Date();
      let data = {
        ...action.payload,
        url: `${action.payload.url}?v=${d.getTime()}`,
      };
      state.productData = updateProductTable(state.productData, data);
      state.loading = false;
    });
    builder.addCase(updateProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProduct.rejected, (state) => {
      state.loading = false;
    });

    // delete product
    builder.addCase(deleteProduct.fulfilled, (state, action) => {
      state.productData = removeDeleteData(state.productData, action.payload);
      state.totalProductCount -= 1;
      state.loading = false;
      state.mode = null;
    });

    builder.addCase(deleteProduct.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(deleteProduct.rejected, (state) => {
      state.loading = false;
    });

    //bulk delete product
    builder.addCase(bulkDeleteProduct.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.productData = state.productData?.filter(
        (prod) => !payload?.includes(prod.id)
      );
      state.totalProductCount -= payload.length;
      state.selectedProductsList = [];
    });

    builder.addCase(bulkDeleteProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(bulkDeleteProduct.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(bulkProductAdd.fulfilled, (state, { payload }) => {
      state.totalProductCount += payload?.length;
      if (payload?.length >= state.productData.length) {
        state.productData = payload.slice(0, state.limit);
      } else {
        state.productData = [
          ...payload,
          ...state.productData.slice(0, state.limit - payload?.length),
        ];
      }

      state.loading = false;
    });
    builder.addCase(bulkProductAdd.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(bulkProductAdd.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const {
  changeMode,
  changeSelectedProduct,
  resetSelectedProduct,
  resetMode,
  changePage,
  setproduct,
  changeSelectedProductBlist,
  updateSelectedProductsBList,
  updateSelectedProductsList,
  changeSelectedProductsList,
  resetSelectedProductsList,
  resetSelectedProductBlist,
  changelimit,
  resetAll,
  removeProductFromProductsBList,
} = productTableSlice.actions;

export default productTableSlice.reducer;
